import { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer, Size } from 'react-virtualized';
import { INDICATOR } from '@voladeum/client';
import { Chart as ChartJS, registerables } from 'chart.js';
import { selectSignal } from 'ducks/signal/selectors';
import { useIndicatorContext } from 'pages/context/IndicatorContext';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useAppSelector } from 'store/hooks';
import { filterTimeseries, getMinMax } from 'utils/arrays';
import { getBarAxis, getLineAxis, getMACDDataset, getTimeseriesAxis } from 'utils/charts';

import 'chartjs-adapter-moment';

import { AdvancedGraphProps } from './types';

import cn from './IndicatorsGraph.module.scss';

ChartJS.register(...registerables);

export const MACDGraph = ({ candles, visible }: AdvancedGraphProps) => {
  const macdRef = useRef<ChartJS>(null);
  const signals = useAppSelector(selectSignal);

  const { indicators: strategy_indicators } = useStrategyContext();
  const { indicators: signal_indicators } = useIndicatorContext();

  const macd = useMemo(() => {
    const combined_indicators = [...strategy_indicators, ...signal_indicators];
    return combined_indicators
      .filter((indicator) => indicator.indicator_id === INDICATOR.MACD)
      .flatMap((indicator) => getMACDDataset(candles, signals, indicator));
  }, [candles, signal_indicators, signals, strategy_indicators]);

  const { min, max } = useMemo(() => {
    const values = macd.flatMap((dataset) => filterTimeseries(dataset.data, visible));
    return getMinMax(values);
  }, [macd, visible]);

  const macd_options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      bar: getBarAxis(),
      line: getLineAxis({ min, max, buffer: 0.2 }),
    },
    plugins: { legend: { display: false } },
  };

  return (
    <AutoSizer>
      {({ height, width }: Size) => (
        <div className={cn.graph} style={{ height, width }}>
          <Chart
            key={`macd-${height}-${width}`}
            ref={macdRef}
            type="line"
            data={{ labels: [], datasets: macd }}
            options={macd_options}
            width={width}
            height={height}
          />
        </div>
      )}
    </AutoSizer>
  );
};
